const DEFAULT_CDN_URL = "https://cdn-dev.culturemap.app";

const normaliseBaseUrl = (value?: string | null): string => {
  if (!value) {
    return DEFAULT_CDN_URL;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return DEFAULT_CDN_URL;
  }

  const withoutTrailingSlash = trimmed.replace(/\s+/g, "").replace(/\/+$/, "");

  try {
    const url = new URL(withoutTrailingSlash);
    return url.origin;
  } catch {
    try {
      const coerced = new URL(`https://${withoutTrailingSlash.replace(/^https?:\/\//, "")}`);
      return coerced.origin;
    } catch {
      return DEFAULT_CDN_URL;
    }
  }
};

// NB: reference `process.env.REACT_APP_CLOUDFRONT_URL` directly — webpack/DefinePlugin
// inlines it as a string literal at build time. A `typeof process` guard here would
// always be false in the browser (process is not defined) and silently fall back to cdn-dev.
const CDN_BASE_URL = normaliseBaseUrl(process.env.REACT_APP_CLOUDFRONT_URL);

export const getEnvCdnBaseUrl = (): string => CDN_BASE_URL;

export const getEnvCdnHostname = (): string => {
  try {
    return new URL(CDN_BASE_URL).hostname;
  } catch {
    return "cdn-dev.culturemap.app";
  }
};

export const isEnvCdnUrl = (value?: string | null): boolean => {
  if (!value) {
    return false;
  }

  const hostname = getEnvCdnHostname();

  try {
    return new URL(value).hostname === hostname;
  } catch {
    return value.includes(hostname);
  }
};

export const buildEnvCdnUrl = (path: string): string => {
  const normalisedPath = path.replace(/^\/*/, "");
  return `${getEnvCdnBaseUrl()}/${normalisedPath}`;
};

/**
 * Resolves a config/icon reference to a loadable URL. Single source of truth for
 * both `<img>` consumers and the map's `styleimagemissing` fetch path:
 *  - absolute `http(s):`/`data:`/`blob:` URLs pass through unchanged
 *  - shared CDN assets (`public-icons/…`) resolve to a signed CloudFront URL
 *  - legacy app-bundled assets (e.g. `icons/…`) resolve against `PUBLIC_URL`
 */
export const resolveIconUrl = (ref?: string | null): string => {
  if (!ref) {
    return "";
  }
  if (/^(https?:\/\/|data:|blob:)/i.test(ref)) {
    return ref;
  }
  const normalisedPath = ref.replace(/^\/+/, "");
  if (/^public-icons\//i.test(normalisedPath)) {
    return buildEnvCdnUrl(normalisedPath);
  }
  const base = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${base}/${normalisedPath}`;
};

// Legacy aliases for existing imports
export const getCdnBaseUrl = getEnvCdnBaseUrl;
export const getCdnHostname = getEnvCdnHostname;
export const isCdnUrl = isEnvCdnUrl;
export const buildCdnUrl = buildEnvCdnUrl;
